import { useTheme } from "@mui/material";
import StellaInspection from "assets/print-templates/inspection.jpg";
import { AppearanceTypeEnum } from "commons/enum/appearance-type";
import { PrintPaperSPUnitEnum } from "commons/enum/foil-unit";
import { numberFormatAsString } from "commons/ultilities";
import { dateTimeDisplay, utcToLocal } from "commons/ultilities/date/dayjs";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useMemo } from "react";
import { ElementToPrint } from "share-components/templates/print/element";
import { PreviewPrinting } from "share-components/templates/print/print-preview";
import {
  createQuantification,
  getLabelOfAppearance,
  getLabelOfConclusionStatus,
  getLabelOfPrintPaperSPUnit,
} from "../share";
const fontWeight = 600;
export const StellaInspectionNotePrintPreview = observer(() => {
  const {
    stellaInspectionNoteManagement: {
      inspectionPrintingManagement: {
        open: openPrint,
        setProp: setPrintManagementProp,
      },
      inspectionNoteDetail,
    },
  } = useStores();
  const onCloseHandler = useCallback(() => {
    setPrintManagementProp("open", false);
  }, [setPrintManagementProp]);
  const theme = useTheme();
  const stellaInspectionElm = useMemo(() => {
    const dateTimeToSign = inspectionNoteDetail?.inspectionDate
      ? utcToLocal(inspectionNoteDetail?.inspectionDate)
      : null;
    const qcPrefixNumber = dateTimeToSign
      ? `PKN${dateTimeToSign?.year()?.toString().slice(-2)}/`
      : "";
    const inspectionElm: ElementToPrint[] = [
      {
        name: "number",
        value: `${qcPrefixNumber} ${inspectionNoteDetail?.number || ""}`,
        left: "16cm",
        top: "3.91cm",
        width: "4.5cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        fontStyle: "italic",
      },
      {
        name: "workOrderNumber",
        value:
          (
            inspectionNoteDetail?.workOrderPO?.woNumber ||
            inspectionNoteDetail?.workOrder?.woNumber
          )?.substring(3) || "",
        left: "3.3cm",
        top: "4.65cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        fontStyle: "italic",
      },
      {
        name: "customerName",
        value:
          inspectionNoteDetail?.workOrderPO?.customerDisplay?.name ||
          inspectionNoteDetail?.workOrder?.customer?.name ||
          "",
        left: "4.71cm",
        top: "6.6cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "inspectionDate",
        value: dateTimeDisplay(inspectionNoteDetail?.inspectionDate) || "",
        left: "4.71cm",
        top: "8.85cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "manufacturingDate",
        value: dateTimeDisplay(inspectionNoteDetail?.manufacturingDate) || "",
        left: "4.71cm",
        top: "9.6cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "expireDate",
        value: dateTimeDisplay(inspectionNoteDetail?.expireDate) || "",
        left: "4.71cm",
        top: "10.35cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "conclusion",
        value: inspectionNoteDetail?.conclusion || "",
        left: "3cm",
        top: "23.05cm",
        width: "17.5cm",
        height: "1.1cm",
        textWrap: "wrap",
        fontWeight: fontWeight,
      },
      {
        name: "day",
        value: dateTimeToSign
          ? ("0" + dateTimeToSign?.date().toString()).slice(-2)
          : "",
        left: "14.96cm",
        top: "24.15cm",
        width: "0.6cm",
        textAlight: "center",
      },
      {
        name: "month",
        value: dateTimeToSign
          ? ("0" + (dateTimeToSign?.month() + 1).toString()).slice(-2)
          : "",
        left: "16.55cm",
        top: "24.15cm",
        width: "0.6cm",
        textAlight: "center",
      },
      {
        name: "year",
        value: dateTimeToSign ? dateTimeToSign?.year().toString() : "",
        left: "17.95cm",
        top: "24.15cm",
        width: "2.8cm",
      },
    ];

    return inspectionElm;
  }, [
    inspectionNoteDetail?.conclusion,
    inspectionNoteDetail?.expireDate,
    inspectionNoteDetail?.inspectionDate,
    inspectionNoteDetail?.manufacturingDate,
    inspectionNoteDetail?.number,
    inspectionNoteDetail?.workOrderPO?.customerDisplay?.name,
    inspectionNoteDetail?.workOrder?.customer?.name,
    inspectionNoteDetail?.workOrder?.woNumber,
    inspectionNoteDetail?.workOrderPO?.woNumber,
  ]);

  const stellaInspectionItemElms = useMemo(() => {
    if (!inspectionNoteDetail?.items) {
      return [];
    }
    const plusMinusOneSuffix = `   ±1 mm`;
    const quantificationSuffix = `   -5/+10 g/m2`;
    const firstItem = inspectionNoteDetail?.items?.[0];
    const inspectionItems: ElementToPrint[] = [
      {
        name: "productName",
        value: firstItem?.product?.name || "",
        left: "4.71cm",
        top: "7.3cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "productCode",
        value: firstItem?.product?.code || "",
        left: "4.71cm",
        top: "8.1cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "appearance1",
        value: "- Màu sắc chuẩn, rõ nét.",
        left: "4.8cm",
        top: "12.5cm",
        width: "7cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "appearance2",
        value: firstItem.appearance
          ? `- ${getLabelOfAppearance(
              firstItem.appearance as AppearanceTypeEnum,
            )}`
          : "",
        left: "4.8cm",
        top: "13.1cm",
        width: "7cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "appearanceResult",
        value: firstItem?.appearanceResult || "",
        left: "12.7cm",
        top: "13cm",
        width: "4.6cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "appearanceConclusion",
        value:
          (firstItem?.appearanceConclusion &&
            getLabelOfConclusionStatus(firstItem?.appearanceConclusion)) ||
          "",
        left: "17.5cm",
        top: "13cm",
        width: "2.8cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "lengthStandard",
        value:
          (firstItem?.product?.dimensionWeight?.lengthStandard &&
            numberFormatAsString(
              firstItem?.product?.dimensionWeight?.lengthStandard?.toLocaleString() +
                plusMinusOneSuffix || "",
            )) ||
          "",
        left: "4.8cm",
        top: "14.7cm",
        width: "6.75cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "right",
      },
      {
        name: "lengthResult",
        value:
          numberFormatAsString(firstItem?.lengthResult?.toString() || "", {
            emtyWhenZero: true,
          }) || "",
        left: "12.7cm",
        top: "14.7cm",
        width: "3.2cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "lengthConclusion",
        value:
          (firstItem?.lengthConclusion &&
            getLabelOfConclusionStatus(firstItem?.lengthConclusion)) ||
          "",
        left: "17.5cm",
        top: "14.7cm",
        width: "2.8cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "widthStandard",
        value:
          (firstItem?.product?.dimensionWeight?.widthStandard &&
            numberFormatAsString(
              firstItem?.product?.dimensionWeight?.widthStandard?.toString() ||
                "",
            ) + plusMinusOneSuffix) ||
          "",
        left: "4.8cm",
        top: "15.48cm",
        width: "6.75cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "right",
      },
      {
        name: "widthResult",
        value:
          numberFormatAsString(firstItem?.widthResult?.toString() || "", {
            emtyWhenZero: true,
          }) || "",
        left: "12.7cm",
        top: "15.48cm",
        width: "3.2cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "widthConclusion",
        value:
          (firstItem?.widthConclusion &&
            getLabelOfConclusionStatus(firstItem?.widthConclusion)) ||
          "",
        left: "17.5cm",
        top: "15.48cm",
        width: "2.8cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "heightStandard",
        value:
          (firstItem?.product?.dimensionWeight?.heightStandard &&
            numberFormatAsString(
              firstItem?.product?.dimensionWeight?.heightStandard?.toString() ||
                "",
            ) + plusMinusOneSuffix) ||
          "",
        left: "4.8cm",
        top: "16.2cm",
        width: "6.75cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "right",
      },
      {
        name: "heightResult",
        value:
          numberFormatAsString(firstItem?.heightResult?.toString() || "", {
            emtyWhenZero: true,
          }) || "",
        left: "12.7cm",
        top: "16.2cm",
        width: "3.2cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "heightConclusion",
        value:
          (firstItem?.heightConclusion &&
            getLabelOfConclusionStatus(firstItem?.heightConclusion)) ||
          "",
        left: "17.5cm",
        top: "16.2cm",
        width: "2.8cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "quantification",
        value:
          firstItem?.product && createQuantification(firstItem?.product) !== ""
            ? createQuantification(firstItem?.product) + quantificationSuffix
            : "",
        left: "4.8cm",
        top: "17.1cm",
        width: "7.2cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "right",
      },
      {
        name: "quantificationResult",
        value:
          numberFormatAsString(
            firstItem?.quantificationResult?.toString() || "",
            {
              emtyWhenZero: true,
            },
          ) || "",
        left: "12.7cm",
        top: "17.1cm",
        width: "3.2cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "quantificationConclusion",
        value:
          (firstItem?.quantificationConclusion &&
            getLabelOfConclusionStatus(firstItem?.quantificationConclusion)) ||
          "",
        left: "17.5cm",
        top: "17.1cm",
        width: "2.8cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "foil",
        value:
          (firstItem?.product?.process?.foil &&
            numberFormatAsString(
              firstItem?.product?.process?.foil?.toString() || "",
              {
                emtyWhenZero: true,
              },
            ) + "   mm") ||
          "",
        left: "4.8cm",
        top: "17.97cm",
        width: "6.75cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "right",
      },
      {
        name: "foilResult",
        value:
          numberFormatAsString(firstItem?.foilResult?.toString() || "", {
            emtyWhenZero: true,
          }) || "",
        left: "12.7cm",
        top: "17.97cm",
        width: "3.2cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "foilConclusion",
        value:
          (firstItem?.foilConclusion &&
            getLabelOfConclusionStatus(firstItem?.foilConclusion)) ||
          "",
        left: "17.5cm",
        top: "17.97cm",
        width: "2.8cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },

      {
        name: "foldingStandardSize",
        value: firstItem.product?.dimensionWeight?.foldingStandardSize
          ? `- ${firstItem.product?.dimensionWeight?.foldingStandardSize}`
          : "",
        left: "4.8cm",
        top: "18.8cm",
        width: "7.5cm",
        height: "0.8cm",
        fontWeight: fontWeight,
      },
      {
        name: "foldingStandardSizeResult",
        value: firstItem?.foldingStandardSizeResult || "",
        left: "12.7cm",
        top: "18.8cm",
        width: "3.2cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "foldingStandardSizeConclusion",
        value:
          (firstItem?.foldingStandardSizeConclusion &&
            getLabelOfConclusionStatus(
              firstItem?.foldingStandardSizeConclusion,
            )) ||
          "",
        left: "17.5cm",
        top: "18.8cm",
        width: "2.8cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },

      {
        name: "foldingStandardCount",
        value: firstItem.product?.dimensionWeight?.foldingStandardCount
          ? `- ${firstItem.product?.dimensionWeight?.foldingStandardCount || ""}`
          : "",
        left: "4.8cm",
        top: "19.6cm",
        width: "7.5cm",
        height: "1cm",
        fontWeight: fontWeight,
      },
      {
        name: "foldingStandardCountResult",
        value: firstItem?.foldingStandardCountResult || "",
        left: "12.7cm",
        top: "19.6cm",
        width: "3.2cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },
      {
        name: "foldingStandardCountConclusion",
        value:
          (firstItem?.foldingStandardCountConclusion &&
            getLabelOfConclusionStatus(
              firstItem?.foldingStandardCountConclusion,
            )) ||
          "",
        left: "17.5cm",
        top: "19.6cm",
        width: "2.8cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
        textAlight: "center",
      },

      {
        name: "printPaperSP",
        value: firstItem?.product?.process?.printPaperSP
          ? `${
              numberFormatAsString(
                firstItem?.product?.process?.printPaperSP?.toString() || "",
              ) || ""
            } ${
              firstItem?.printPaperSPUnit
                ? getLabelOfPrintPaperSPUnit(
                    firstItem?.printPaperSPUnit as PrintPaperSPUnitEnum,
                  )
                : ""
            }`
          : "",
        left: "5.5cm",
        top: "21.2cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
      {
        name: "amount",
        value: firstItem?.amount || "",
        left: "5.5cm",
        top: "21.9cm",
        width: "15cm",
        textWrap: "nowrap",
        fontWeight: fontWeight,
      },
    ];

    return inspectionItems;
  }, [inspectionNoteDetail?.items]);

  const stellaElements = useMemo(() => {
    return [...stellaInspectionElm, ...stellaInspectionItemElms];
  }, [stellaInspectionElm, stellaInspectionItemElms]);

  return (
    <PreviewPrinting
      open={openPrint}
      printIncludeTemplate={true}
      elements={stellaElements}
      templateImage={StellaInspection}
      backgroundSize="100%"
      backgroundPosition={"inherit"}
      mode="portrait"
      fontSize="16px"
      paperSize={{
        width: "21cm",
        height: "29.7cm",
      }}
      sx={{
        fontFamily: `"Times New Roman", Times, ${theme.typography.fontFamily} !important`,
      }}
      onClose={onCloseHandler}
    />
  );
});
